import React, { useRef, useEffect } from 'react';
import { FolderPlus, FileSpreadsheet, FileText, Search, Bookmark, ExternalLink, Upload, Loader2, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

const ItemCreationPanel = ({
    isAddingLinkOrTask,
    isAddingSheet,
    isAddingDoc,
    loading,
    newTaskOrLinkName,
    handleTaskOrLinkChange,
    handleCreateLinkOrTask,
    handleAddLinkOrTaskClick,
    handleAddSheet,
    handleAddDoc,
    setIsAddingLinkOrTask,
    setIsAddingSheet,
    setIsAddingDoc,
    focusedButton,
    folderId,
    isBookmarked,
    onToggleBookmark,
    gdriveFolderId,
    driveSyncing,
    onRefreshDriveFiles,
    onTsvUploadClick
}) => {
    const inputRef = useRef(null);

    // Auto-focus when input mode is activated
    useEffect(() => {
        if ((isAddingLinkOrTask || isAddingSheet || isAddingDoc) && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isAddingLinkOrTask, isAddingSheet, isAddingDoc]);

    // Auto-resize textarea based on content
    useEffect(() => {
        if (inputRef.current) {
            // Reset height to auto to get the correct scrollHeight
            inputRef.current.style.height = 'auto';
            // Set height to scrollHeight, with min and max constraints
            const scrollHeight = inputRef.current.scrollHeight;
            const minHeight = 32; // Minimum height (h-8 equivalent)
            const maxHeight = 200; // Maximum height before scrolling
            inputRef.current.style.height = `${Math.min(Math.max(scrollHeight, minHeight), maxHeight)}px`;
        }
    }, [newTaskOrLinkName, isAddingLinkOrTask, isAddingSheet, isAddingDoc]);

    // Input mode for creating new items
    if (isAddingLinkOrTask || isAddingSheet || isAddingDoc) {
        const inputLabel = isAddingSheet ? "Sheet Name" : isAddingDoc ? "Doc Name" : "Folder Name or Link";
        const isCreatingSheetOrDoc = loading && (isAddingSheet || isAddingDoc);
        
        return (
            <div className="flex flex-col gap-1.5 py-2 px-2">
                <Label htmlFor="creation-input" className="text-xs text-muted-foreground">
                    {inputLabel}
                </Label>
                <div className="relative">
                    <Textarea
                        ref={inputRef}
                        id="creation-input"
                        spellCheck={false}
                        autoComplete="off"
                        placeholder={`Enter ${inputLabel.toLowerCase()}...`}
                        value={newTaskOrLinkName}
                        onChange={handleTaskOrLinkChange}
                        onKeyDown={handleCreateLinkOrTask}
                        onBlur={() => {
                            if (!loading) {
                                setIsAddingLinkOrTask(false)
                                setIsAddingSheet(false)
                                setIsAddingDoc(false)
                            }
                        }}
                        disabled={isCreatingSheetOrDoc}
                        className={cn(
                            "min-h-8 max-h-[200px] text-sm resize-none overflow-y-auto",
                            isCreatingSheetOrDoc && "opacity-60 cursor-not-allowed"
                        )}
                        rows={1}
                    />
                    {isCreatingSheetOrDoc && (
                        <div className="absolute inset-0 flex items-center justify-center bg-background/50 rounded-md">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                                <span>Creating {isAddingSheet ? 'Sheet' : 'Doc'}...</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    if (loading) {
        return <div className="flex items-center justify-center py-2 text-sm text-muted-foreground">Creating...</div>;
    }

    return (
        <div className="flex items-center justify-between gap-1 px-2 py-1.5">
            {/* Creation buttons */}
            <div className="flex gap-1 flex-1">
                <button
                    onClick={handleAddLinkOrTaskClick}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddLinkOrTaskClick();
                        }
                    }}
                    className={cn(
                        "flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 text-xs font-medium rounded border transition-colors",
                        "bg-card text-card-foreground border-border hover:bg-accent hover:border-accent-foreground/20",
                        focusedButton === 'folder' && "ring-2 ring-ring ring-offset-1 ring-offset-background"
                    )}
                >
                    <FolderPlus className="w-3.5 h-3.5" />
                    Folder
                </button>
                <button
                    onClick={handleAddSheet}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddSheet();
                        }
                    }}
                    className={cn(
                        "flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 text-xs font-medium rounded border transition-colors",
                        "bg-card text-card-foreground border-border hover:bg-accent hover:border-accent-foreground/20",
                        focusedButton === 'sheet' && "ring-2 ring-ring ring-offset-1 ring-offset-background"
                    )}
                >
                    <FileSpreadsheet className="w-3.5 h-3.5" />
                    Sheet
                </button>
                <button
                    onClick={handleAddDoc}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddDoc();
                        }
                    }}
                    className={cn(
                        "flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 text-xs font-medium rounded border transition-colors",
                        "bg-card text-card-foreground border-border hover:bg-accent hover:border-accent-foreground/20",
                        focusedButton === 'doc' && "ring-2 ring-ring ring-offset-1 ring-offset-background"
                    )}
                >
                    <FileText className="w-3.5 h-3.5" />
                    Doc
                </button>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-0.5">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            onClick={onTsvUploadClick}
                            className="p-1.5 rounded border border-border bg-card text-card-foreground hover:bg-accent transition-colors"
                        >
                            <Upload className="w-3.5 h-3.5" />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent>Upload TSV to create folder structure</TooltipContent>
                </Tooltip>
                {folderId && (
                    <>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Link
                                    to={`/search?ancestor_id=${folderId}`}
                                    className="p-1.5 rounded border border-border bg-card text-card-foreground hover:bg-accent transition-colors inline-flex"
                                >
                                    <Search className="w-3.5 h-3.5" />
                                </Link>
                            </TooltipTrigger>
                            <TooltipContent>Search within this folder</TooltipContent>
                        </Tooltip>
                        {gdriveFolderId && (
                            <>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button
                                            onClick={() => window.open(`https://drive.google.com/drive/folders/${gdriveFolderId}`, '_blank')}
                                            className="p-1.5 rounded border border-border bg-card text-card-foreground hover:bg-accent transition-colors"
                                        >
                                            <ExternalLink className="w-3.5 h-3.5" />
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent>Open Google Drive files</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button
                                            onClick={onRefreshDriveFiles}
                                            disabled={driveSyncing}
                                            className="p-1.5 rounded border border-border bg-card text-card-foreground hover:bg-accent transition-colors disabled:opacity-60"
                                        >
                                            <RefreshCw className={cn("w-3.5 h-3.5", driveSyncing && "animate-spin")} />
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent>Refresh Google Drive files</TooltipContent>
                                </Tooltip>
                            </>
                        )}
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    onClick={onToggleBookmark}
                                    className={cn(
                                        "p-1.5 rounded border transition-colors",
                                        isBookmarked
                                            ? "bg-primary/10 border-primary/30 text-primary"
                                            : "bg-card border-border text-card-foreground hover:bg-accent"
                                    )}
                                >
                                    <Bookmark className={cn("w-3.5 h-3.5", isBookmarked && "fill-current")} />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent>{isBookmarked ? "Remove bookmark" : "Bookmark this folder"}</TooltipContent>
                        </Tooltip>
                    </>
                )}
            </div>
        </div>
    );
};

export default ItemCreationPanel;

